"use client"

import Link from "next/link"
import { ArrowRight, CheckCircle, Loader2, Mail } from "lucide-react"

import { AuthFooter } from "@/components/layout"
import { DzpLogo } from "@/components/layout/DzpLogo"
import { Input } from "@/components/ui"

import { LoginProvider, useLoginContext } from "./context"
import { useLoginController } from "./controller"
import { styles } from "./styles"

interface LoginViewProps {
  redirectTo: string
  authError?: string | null
}

const AUTH_ERROR_MESSAGES: Record<string, string> = {
  otp_expired:
    "Este link de acesso expirou ou já foi usado. Digite seu email abaixo e enviaremos um novo link.",
  auth: "Erro na autenticação. Tente novamente.",
}

function GoogleIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  )
}

function LoginForm({ redirectTo, authError }: LoginViewProps) {
  const {
    email,
    setEmail,
    isLoading,
    isGoogleLoading,
    isMagicLinkSent,
    error,
    setIsMagicLinkSent,
    setError,
  } = useLoginContext()

  const { handleGoogleLogin, handleMagicLinkLogin } =
    useLoginController(redirectTo)

  if (isMagicLinkSent) {
    return (
      <div className={styles.successContainer}>
        <div className={styles.successIcon}>
          <CheckCircle className="h-8 w-8 text-success" />
        </div>
        <h2 className={styles.successTitle}>Verifique seu email</h2>
        <p className={styles.successText}>
          Enviamos um link de acesso para
          <br />
          <span className={styles.successEmail}>{email}</span>
        </p>
        <p className={styles.successHint}>
          Não encontrou? Verifique também a caixa de spam ou lixo eletrônico.
        </p>
        <button
          onClick={() => {
            setIsMagicLinkSent(false)
            setEmail("")
            setError(null)
          }}
          className={styles.changeEmailButton}
        >
          Usar outro email
        </button>
      </div>
    )
  }

  return (
    <>
      {/*
        No branch on the error text: showing a signup link only for unknown
        addresses turned the form into an account-existence oracle. The link
        lives below the form instead, where it reveals nothing.
      */}
      {(error || authError) && (
        <div className={styles.errorBox}>
          <p className={styles.errorText}>
            {error ||
              AUTH_ERROR_MESSAGES[authError ?? ""] ||
              AUTH_ERROR_MESSAGES.auth}
          </p>
        </div>
      )}

      <button
        onClick={handleGoogleLogin}
        disabled={isGoogleLoading}
        className={styles.googleButton}
        aria-label={
          isGoogleLoading
            ? "Autenticando com Google..."
            : "Continuar com Google"
        }
      >
        {isGoogleLoading ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : (
          <GoogleIcon />
        )}
        Continuar com Google
      </button>

      <div className={styles.divider}>
        <div className={styles.dividerLine} />
        <span className={styles.dividerText}>ou</span>
        <div className={styles.dividerLine} />
      </div>

      <form onSubmit={handleMagicLinkLogin}>
        <div className="mb-4">
          <label className={styles.label}>Email</label>
          <Input
            type="email"
            icon={Mail}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="seu@email.com"
            name="email"
            autoComplete="email"
            required
          />
        </div>
        <button
          type="submit"
          disabled={isLoading || !email}
          className={styles.submitButton}
        >
          {isLoading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <>
              Enviar link de acesso
              <ArrowRight size={18} />
            </>
          )}
        </button>
      </form>

      <p className={styles.footer}>
        Não tem uma conta?{" "}
        <Link href="/cadastro" className={styles.footerLink}>
          Cadastre-se grátis
        </Link>
      </p>
    </>
  )
}

export function LoginView({ redirectTo, authError }: LoginViewProps) {
  return (
    <div className="flex min-h-screen flex-col bg-gray-50 px-4 py-8">
      <div className="flex flex-1 items-center justify-center">
        <div className={styles.wrapper}>
          <div className={styles.logoSection}>
            <DzpLogo />
            <h1 className={styles.title}>Bem-vindo de volta</h1>
            <p className={styles.subtitle}>
              Entre na sua conta para continuar aprendendo
            </p>
          </div>

          <div className={styles.card}>
            <LoginProvider>
              <LoginForm redirectTo={redirectTo} authError={authError} />
            </LoginProvider>
          </div>

          <p className={styles.backLink}>
            <Link href="/" className={styles.backLinkText}>
              ← Voltar para o início
            </Link>
          </p>
        </div>
      </div>

      <AuthFooter />
    </div>
  )
}
